import React, { useState } from "react";
import { DollarSign, X, Heart } from "lucide-react";

const PRESETS = [0.15, 0.20, 0.25];

export default function TipPicker({ open, servicePrice, onClose, onConfirm, busy }) {
  const [choice, setChoice] = useState(0.20);
  const [custom, setCustom] = useState("");
  const [mode, setMode] = useState("preset"); // preset | custom | none

  if (!open) return null;

  let tip = 0;
  if (mode === "preset") tip = Math.round(servicePrice * choice * 100) / 100;
  else if (mode === "custom") tip = Math.max(0, parseFloat(custom) || 0);
  const total = Math.round((servicePrice + tip) * 100) / 100;

  const handleConfirm = () => {
    onConfirm(tip);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="tip-picker">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border-2 border-asap-deep shadow-hard max-w-md w-full">
        <div className="bg-asap-deep text-white px-5 py-4 flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-asap-yellow font-bold flex items-center gap-1"><Heart size={12}/> Support your driver</div>
            <div className="font-display text-xl uppercase">Add a tip</div>
          </div>
          <button onClick={onClose} data-testid="tip-close" className="p-1 hover:bg-white/10 rounded"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">100% of your tip goes to the driver.</p>

          <div className="grid grid-cols-3 gap-2">
            {PRESETS.map((p) => {
              const active = mode === "preset" && choice === p;
              const amt = Math.round(servicePrice * p * 100) / 100;
              return (
                <button
                  key={p}
                  onClick={() => { setMode("preset"); setChoice(p); }}
                  data-testid={`tip-preset-${Math.round(p * 100)}`}
                  className={`p-4 border-2 text-center transition-all ${active ? "bg-asap-yellow border-asap-deep shadow-hard-sm" : "border-slate-300 hover:border-asap-orange"}`}
                >
                  <div className="font-display text-2xl text-asap-deep">{Math.round(p * 100)}%</div>
                  <div className="text-xs font-bold text-asap-orange mt-1">${amt.toFixed(2)}</div>
                </button>
              );
            })}
          </div>

          <div>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-1">Custom amount</div>
              <div className="flex items-center border-2 border-asap-deep">
                <div className="px-3 text-asap-orange"><DollarSign size={18}/></div>
                <input
                  data-testid="tip-custom"
                  type="number"
                  step="0.50"
                  min="0"
                  value={custom}
                  onChange={(e) => { setCustom(e.target.value); setMode("custom"); }}
                  placeholder="0.00"
                  className="flex-1 py-3 outline-none"
                />
              </div>
            </label>
          </div>

          <button
            onClick={() => setMode("none")}
            data-testid="tip-skip"
            className={`w-full py-2 text-sm font-bold uppercase tracking-widest border-2 ${mode === "none" ? "border-asap-deep bg-asap-yellow" : "border-slate-300 text-slate-500 hover:border-slate-400"}`}
          >
            No tip
          </button>

          <div className="bg-asap-yellow/20 border-2 border-asap-yellow p-4">
            <div className="flex justify-between text-sm text-slate-600">
              <span>Service</span><span>${servicePrice.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-slate-600 mt-1">
              <span>Tip</span><span data-testid="tip-summary">${tip.toFixed(2)}</span>
            </div>
            <div className="border-t border-asap-deep/30 mt-2 pt-2 flex justify-between items-baseline">
              <span className="font-display uppercase text-asap-deep">Total</span>
              <span className="font-display text-2xl text-asap-orange" data-testid="tip-total">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="p-4 border-t-2 border-asap-deep flex gap-3 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 border-2 border-slate-300 font-bold uppercase text-sm hover:border-asap-deep">Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={busy}
            data-testid="tip-confirm"
            className="flex-1 px-4 py-2 bg-asap-yellow text-asap-deep font-display uppercase border-2 border-asap-deep shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60"
          >
            {busy ? "Redirecting…" : `Pay $${total.toFixed(2)} →`}
          </button>
        </div>
      </div>
    </div>
  );
}
